/**
 * OpenDesignConnectModal — manual Connect form for the OpenDesign connector.
 *
 * OpenDesign auth is an INSTANCE URL + API TOKEN (the OD_API_TOKEN bearer),
 * NOT OAuth. The user provides TWO values:
 *   - Instance URL  (baseUrl of a deployed OpenDesign instance)
 *   - API token     (the OD_API_TOKEN bearer used to authenticate)
 *
 * SMART PREFILL: on open we hit GET /integrations/open-design/suggest. When the
 * backend has auto-discovered the project's own deployed OpenDesign instance
 * ({ available:true, baseUrl, token }) we prefill both fields (user can still
 * edit/clear) and show a hint — making "connect my own instance" one click.
 *
 * On Connect we POST /integrations/open-design { baseUrl, token }. The backend
 * validates + persists; structured 4xx errors are surfaced inline from
 * err.body.error / err.body.message (ApiClient attaches .body + .status to the
 * thrown error). Disconnect is DELETE /integrations/open-design.
 *
 * Mirrors PlaneConnectModal's shell (two fields + hint) but talks to the api
 * service directly (api.get/post/delete) per the frozen REST contract on the
 * main api-prod RestApi (/integrations/open-design*).
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import api from '../../services/api';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function OpenDesignConnectModal({ isOpen, onClose, onConnected }) {
  const [baseUrl, setBaseUrl] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  // True once GET /integrations/open-design/suggest prefilled the fields, so we
  // can show the "auto-detected" hint. Cleared if the user edits a field.
  const [prefilled, setPrefilled] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  // On open: reset, then ask the backend to suggest the project's own deployed
  // OpenDesign instance and prefill if available.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setBaseUrl('');
    setToken('');
    setError('');
    setConnecting(false);
    setPrefilled(false);
    setSuggesting(true);
    api.get('/integrations/open-design/suggest')
      .then((data) => {
        if (cancelled) return;
        if (data && data.available) {
          if (data.baseUrl) setBaseUrl(data.baseUrl);
          if (data.token) setToken(data.token);
          if (data.baseUrl || data.token) setPrefilled(true);
        }
      })
      .catch(() => {
        // Suggestion is best-effort — a 404/empty just means no auto-detected
        // instance. Leave the form blank for manual entry; never surface this.
      })
      .finally(() => {
        if (!cancelled) setSuggesting(false);
      });
    return () => { cancelled = true; };
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!baseUrl.trim()) {
      setError('Instance URL is required.');
      return;
    }
    if (!token.trim()) {
      setError('API token is required.');
      return;
    }
    setConnecting(true);
    setError('');
    try {
      await api.post('/integrations/open-design', {
        baseUrl: baseUrl.trim(),
        token: token.trim(),
      });
      setConnecting(false);
      onConnected?.();
      onClose?.();
    } catch (err) {
      setConnecting(false);
      setError(
        err?.body?.error
        || err?.body?.message
        || err?.message
        || 'Failed to connect OpenDesign. Please check the instance URL and API token.',
      );
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
      aria-label="Connect OpenDesign"
    >
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect OpenDesign</h2>
        <p className="lark-modal-description">
          Connect a deployed OpenDesign instance so agents can read designs,
          export PDFs, and drive its Local CLI. Enter your instance URL and its
          API token (the <code>OD_API_TOKEN</code> bearer) below.
        </p>

        {prefilled && (
          <p
            className="lark-form-hint"
            role="status"
            style={{ fontSize: 12, color: 'var(--ion-color-success, #2dd36f)', marginTop: -4, marginBottom: 8 }}
          >
            Auto-detected your deployed OpenDesign instance — review and connect.
          </p>
        )}

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="open-design-base-url">Instance URL</label>
          <input
            id="open-design-base-url"
            type="text"
            value={baseUrl}
            placeholder="https://your-opendesign-instance"
            onChange={(e) => { setBaseUrl(e.target.value || ''); setError(''); setPrefilled(false); }}
            className="lark-form-input"
            disabled={connecting || suggesting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="open-design-token">API Token</label>
          <input
            id="open-design-token"
            type="password"
            value={token}
            placeholder="OD_API_TOKEN"
            onChange={(e) => { setToken(e.target.value || ''); setError(''); setPrefilled(false); }}
            className="lark-form-input"
            disabled={connecting || suggesting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        {error && <p className="lark-modal-error" role="alert">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || suggesting || !baseUrl.trim() || !token.trim()}
            aria-label="Submit OpenDesign credentials"
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
            aria-label="Cancel OpenDesign connection"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
